/**
 * DatePicker (U5): typed entry (M/D/YYYY or YYYY-MM-DD) + glass calendar
 * popover with keyboard navigation (arrows, PageUp/Down, Enter, Escape).
 * Value is an ISO `YYYY-MM-DD` string (or null) — no timezone drift.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from './cx';
import { Field } from './Field';
import { IconCalendar, IconChevronLeft, IconChevronRight } from './icons';
import type { ControlSize } from './Button';

export interface DatePickerProps {
  /** ISO `YYYY-MM-DD` or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  full?: boolean;
  id?: string;
  min?: string;
  max?: string;
  'aria-label'?: string;
}

interface DateParts {
  y: number;
  m: number; // 1–12
  d: number; // 1–31
}

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(p: DateParts): string {
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

function parseIso(iso: string): DateParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const parts = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  return isValid(parts) ? parts : null;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function isValid(p: DateParts): boolean {
  return p.m >= 1 && p.m <= 12 && p.d >= 1 && p.d <= daysInMonth(p.y, p.m);
}

function addDays(p: DateParts, delta: number): DateParts {
  const dt = new Date(Date.UTC(p.y, p.m - 1, p.d + delta));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

function addMonths(p: DateParts, delta: number): DateParts {
  const dt = new Date(Date.UTC(p.y, p.m - 1 + delta, 1));
  const y = dt.getUTCFullYear();
  const m = dt.getUTCMonth() + 1;
  return { y, m, d: Math.min(p.d, daysInMonth(y, m)) };
}

function dayOfWeek(p: DateParts): number {
  return new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
}

function today(): DateParts {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

function formatDisplay(p: DateParts): string {
  return `${MONTHS[p.m - 1]?.slice(0, 3)} ${p.d}, ${p.y}`;
}

/** Accepts `M/D/YYYY`, `M-D-YYYY`, or ISO. */
function parseTyped(raw: string): DateParts | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const iso = parseIso(trimmed);
  if (iso) return iso;
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(trimmed);
  if (us) {
    const parts = { y: Number(us[3]), m: Number(us[1]), d: Number(us[2]) };
    if (isValid(parts)) return parts;
  }
  return 'invalid';
}

function inRange(p: DateParts, min?: string, max?: string): boolean {
  const iso = toIso(p);
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}

export function DatePicker({
  value,
  onChange,
  label,
  hint,
  error,
  size = 'md',
  placeholder = 'M/D/YYYY',
  disabled,
  required,
  full,
  id,
  min,
  max,
  'aria-label': ariaLabel,
}: DatePickerProps): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedById = hint != null || error ? `${inputId}-desc` : undefined;

  const selected = value ? parseIso(value) : null;
  const [open, setOpen] = useState(false);
  const [focusDay, setFocusDay] = useState<DateParts>(selected ?? today());
  const [text, setText] = useState(selected ? formatDisplay(selected) : '');
  const [editing, setEditing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Keep rest display in sync with external value when not typing.
  const selectedIso = selected ? toIso(selected) : null;
  useEffect(() => {
    if (!editing) {
      const parts = selectedIso ? parseIso(selectedIso) : null;
      setText(parts ? formatDisplay(parts) : '');
    }
  }, [selectedIso, editing]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function commitTyped(): void {
    const parsed = parseTyped(text);
    if (parsed === 'invalid') {
      setText(selected ? formatDisplay(selected) : '');
      return;
    }
    if (parsed === null) {
      if (value !== null) onChange(null);
      return;
    }
    if (!inRange(parsed, min, max)) {
      setText(selected ? formatDisplay(selected) : '');
      return;
    }
    onChange(toIso(parsed));
    setText(formatDisplay(parsed));
  }

  function openCalendar(): void {
    if (disabled) return;
    setFocusDay(selected ?? today());
    setOpen(true);
  }

  function pick(p: DateParts): void {
    if (!inRange(p, min, max)) return;
    onChange(toIso(p));
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleGridKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const moves: Record<string, () => DateParts> = {
      ArrowLeft: () => addDays(focusDay, -1),
      ArrowRight: () => addDays(focusDay, 1),
      ArrowUp: () => addDays(focusDay, -7),
      ArrowDown: () => addDays(focusDay, 7),
      PageUp: () => addMonths(focusDay, -1),
      PageDown: () => addMonths(focusDay, 1),
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setFocusDay(move());
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick(focusDay);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      inputRef.current?.focus();
    }
  }

  // Focus the roving day button whenever the calendar opens or focus moves.
  useEffect(() => {
    if (!open) return;
    const el = gridRef.current?.querySelector<HTMLButtonElement>('[data-roving="true"]');
    el?.focus();
  }, [open, focusDay]);

  const monthStart: DateParts = { y: focusDay.y, m: focusDay.m, d: 1 };
  const leading = dayOfWeek(monthStart);
  const gridStart = addDays(monthStart, -leading);
  const cells: DateParts[] = [];
  for (let i = 0; i < 42; i += 1) cells.push(addDays(gridStart, i));
  const todayIso = toIso(today());

  return (
    <Field
      htmlFor={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      full={full}
      describedById={describedById}
    >
      <div className="bl-datepicker" ref={rootRef}>
        <span
          className={cx(
            'bl-control',
            `bl-control--${size}`,
            full && 'bl-control--full',
            Boolean(error) && 'is-invalid',
            disabled && 'is-disabled',
          )}
        >
          <input
            ref={inputRef}
            id={inputId}
            className="bl-control__input"
            value={text}
            placeholder={placeholder}
            autoComplete="off"
            disabled={disabled}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedById}
            aria-label={ariaLabel}
            onFocus={() => setEditing(true)}
            onBlur={(event) => {
              if (!rootRef.current?.contains(event.relatedTarget as Node)) {
                commitTyped();
                setEditing(false);
              }
            }}
            onChange={(event) => setText(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commitTyped();
                setEditing(false);
              } else if (event.key === 'ArrowDown' && !open) {
                event.preventDefault();
                openCalendar();
              } else if (event.key === 'Escape' && open) {
                setOpen(false);
              }
            }}
          />
          <button
            type="button"
            className="bl-datepicker__toggle"
            aria-label={open ? 'Close calendar' : 'Open calendar'}
            aria-expanded={open}
            disabled={disabled}
            tabIndex={-1}
            onClick={() => (open ? setOpen(false) : openCalendar())}
          >
            <IconCalendar size={15} />
          </button>
        </span>

        {open && (
          <div className="bl-popover bl-calendar" role="dialog" aria-label="Choose date">
            <div className="bl-calendar__head">
              <button
                type="button"
                className="bl-calendar__nav"
                aria-label="Previous month"
                onClick={() => setFocusDay(addMonths(focusDay, -1))}
              >
                <IconChevronLeft size={14} />
              </button>
              <span className="bl-calendar__month">
                {MONTHS[focusDay.m - 1]} {focusDay.y}
              </span>
              <button
                type="button"
                className="bl-calendar__nav"
                aria-label="Next month"
                onClick={() => setFocusDay(addMonths(focusDay, 1))}
              >
                <IconChevronRight size={14} />
              </button>
            </div>
            <div
              className="bl-calendar__grid"
              role="grid"
              ref={gridRef}
              onKeyDown={handleGridKeyDown}
            >
              {DOW.map((d) => (
                <span key={d} className="bl-calendar__dow" role="columnheader">
                  {d}
                </span>
              ))}
              {cells.map((cell) => {
                const iso = toIso(cell);
                const isFocus = iso === toIso(focusDay);
                const selectable = inRange(cell, min, max);
                return (
                  <button
                    key={iso}
                    type="button"
                    role="gridcell"
                    data-roving={isFocus ? 'true' : undefined}
                    tabIndex={isFocus ? 0 : -1}
                    className={cx(
                      'bl-calendar__day',
                      cell.m !== focusDay.m && 'is-outside',
                      iso === todayIso && 'is-today',
                    )}
                    aria-selected={value === iso || undefined}
                    aria-label={`${formatDisplay(cell)}`}
                    disabled={!selectable}
                    onClick={() => pick(cell)}
                  >
                    {cell.d}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Field>
  );
}
